import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ButtonComponent,
  CheckboxComponent,
  EmptyComponent,
  SkeletonComponent,
} from 'nebula-ui-kit';
import { finalize, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NotificationsService } from '../../../../data-access/src/lib/notifications/notifications.service';
import type { VaultNotification } from '../../../../domain/src/lib/notifications/notification.model';
import {
  FieldLabelComponent,
  GridFormComponent,
  GridFormItemComponent,
  applyVaultModuleInitialLoadErrorSignals,
  PageHeaderComponent,
  toModuleAccessErrorMessage,
  VaultModuleShellComponent,
  VaultPageAlertComponent,
} from '@nebula-shared/ui';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    PageHeaderComponent,
    GridFormComponent,
    GridFormItemComponent,
    FieldLabelComponent,
    CheckboxComponent,
    ButtonComponent,
    EmptyComponent,
    SkeletonComponent,
    VaultModuleShellComponent,
    VaultPageAlertComponent,
  ],
})
export class NotificationsComponent {
  private readonly notificationsService = inject(NotificationsService);
  private readonly destroyRef = inject(DestroyRef);

  private static readonly MODULE_LABEL = 'Notificaciones';

  readonly loading = signal(true);
  readonly busyId = signal<string | null>(null);
  readonly markingAll = signal(false);
  readonly accessDenied = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly loadErrorTitle = signal('');
  readonly error = signal<string | null>(null);

  readonly unreadOnly = signal(false);
  readonly items = signal<VaultNotification[]>([]);

  readonly unreadCount = computed(
    () => this.items().filter((n) => !(n.read ?? n.is_read)).length,
  );

  readonly visibleItems = computed(() => {
    const list = this.items();
    if (!this.unreadOnly()) return list;
    return list.filter((n) => !(n.read ?? n.is_read));
  });

  constructor() {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);
    this.accessDenied.set(false);
    this.loadError.set(null);
    this.notificationsService
      .list(false)
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (rows) => this.items.set(rows),
        error: (err: unknown) =>
          applyVaultModuleInitialLoadErrorSignals(
            err,
            {
              moduleLabel: NotificationsComponent.MODULE_LABEL,
              fallback: 'No se pudieron cargar las notificaciones.',
            },
            {
              accessDenied: this.accessDenied,
              loadError: this.loadError,
              loadErrorTitle: this.loadErrorTitle,
            },
          ),
      });
  }

  setUnreadOnly(checked: boolean): void {
    this.unreadOnly.set(checked);
  }

  markRead(n: VaultNotification): void {
    if (n.read ?? n.is_read) return;
    this.busyId.set(n.id);
    this.notificationsService
      .markAsRead(n.id)
      .pipe(
        finalize(() => this.busyId.set(null)),
        catchError(() => of(null)),
      )
      .subscribe({
        next: () =>
          this.items.update((list) =>
            list.map((x) =>
              x.id === n.id ? { ...x, read: true, is_read: true } : x,
            ),
          ),
        error: (err: unknown) =>
          this.error.set(
            toModuleAccessErrorMessage(err, {
              moduleLabel: 'Notificaciones',
              fallback: 'No se pudo marcar como leída.',
            }),
          ),
      });
  }

  markAllRead(): void {
    this.markingAll.set(true);
    this.notificationsService
      .markAllAsRead()
      .pipe(finalize(() => this.markingAll.set(false)))
      .subscribe({
        next: () =>
          this.items.update((list) =>
            list.map((x) => ({ ...x, read: true, is_read: true })),
          ),
        error: (err: unknown) =>
          this.error.set(
            toModuleAccessErrorMessage(err, {
              moduleLabel: 'Notificaciones',
              fallback: 'No se pudieron marcar todas como leídas.',
            }),
          ),
      });
  }

  formatWhen(iso: string): string {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  }
}
